/**
 * OAuth Authentication Service
 *
 * Provides functions for signing in with Apple/Google OAuth providers
 * and linking existing offline accounts to OAuth identities.
 */

import { supabase } from './supabase';
import type { AuthError } from '@supabase/supabase-js';

export type OAuthProvider = 'google' | 'apple';

interface AuthResult {
  error: Error | null;
}

/**
 * Get the OAuth redirect URL based on the current environment
 */
function getRedirectUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * Convert Supabase AuthError to standard Error
 */
function toError(authError: AuthError | null): Error | null {
  if (!authError) return null;
  return new Error(authError.message);
}

/**
 * Sign in with Google OAuth
 * Initiates OAuth flow - redirects user to Google sign-in page
 */
export async function signInWithGoogle(): Promise<AuthResult> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: getRedirectUrl(),
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
    },
  });

  return { error: toError(error) };
}

/**
 * Sign in with Apple OAuth
 * Initiates OAuth flow - redirects user to Apple sign-in page
 */
export async function signInWithApple(): Promise<AuthResult> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: getRedirectUrl(),
    },
  });

  return { error: toError(error) };
}

/**
 * Link an existing offline account with Google OAuth
 * This upgrades an anonymous/offline account to a linked account
 */
export async function linkGoogleAccount(): Promise<AuthResult> {
  try {
    const { error } = await supabase.auth.linkIdentity({
      provider: 'google',
      options: {
        redirectTo: getRedirectUrl(),
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    return { error: toError(error) };
  } catch (err) {
    // linkIdentity may throw if user is not authenticated
    // In that case, fall back to signInWithGoogle
    console.warn('[auth] linkIdentity failed, falling back to signIn:', err);
    return signInWithGoogle();
  }
}

/**
 * Link an existing offline account with Apple OAuth
 * This upgrades an anonymous/offline account to a linked account
 */
export async function linkAppleAccount(): Promise<AuthResult> {
  try {
    const { error } = await supabase.auth.linkIdentity({
      provider: 'apple',
      options: {
        redirectTo: getRedirectUrl(),
      },
    });

    return { error: toError(error) };
  } catch (err) {
    // linkIdentity may throw if user is not authenticated
    // In that case, fall back to signInWithApple
    console.warn('[auth] linkIdentity failed, falling back to signIn:', err);
    return signInWithApple();
  }
}

/**
 * Sign out the current user
 * Clears the Supabase session
 */
export async function signOut(): Promise<AuthResult> {
  const { error } = await supabase.auth.signOut();
  return { error: toError(error) };
}

/**
 * Get the current Supabase session
 */
export async function getSession() {
  return await supabase.auth.getSession();
}

/**
 * Get the current Supabase user
 */
export async function getUser() {
  return await supabase.auth.getUser();
}

/**
 * Exchange OAuth code for session (used in callback)
 */
export async function exchangeCodeForSession(code: string) {
  return await supabase.auth.exchangeCodeForSession(code);
}

/**
 * Listen for auth state changes
 */
export function onAuthStateChange(
  callback: (event: string, session: unknown) => void
) {
  return supabase.auth.onAuthStateChange(callback);
}

/**
 * Get the provider name from session data
 */
export function getProviderFromSession(session: {
  user?: {
    app_metadata?: {
      provider?: string;
      providers?: string[];
    };
  };
}): OAuthProvider | null {
  const provider = session?.user?.app_metadata?.provider;
  const providers = session?.user?.app_metadata?.providers;

  // Check direct provider first
  if (provider === 'google' || provider === 'apple') {
    return provider;
  }

  // Check providers array
  if (providers?.includes('google')) return 'google';
  if (providers?.includes('apple')) return 'apple';

  return null;
}
